import { placeBounds } from '../../../shared/world/rooms';
import { MAX_SPEED } from '../../../shared/limits';
import type { Anim, PlaceId } from '../../../shared/ids';
import type { PosFields } from '../../../shared/protocol';
import { HOOKS } from '../../3-store/core/hooks';
import { peers, placePeer, tickPeer, type Peer } from '../../3-store/core/peers';

const EXTRAP_MAX = 0.2, ERR_TAU = 0.1, GRAVITY = 1250;   // 중력은 js/town.js와 같은 값

// 내 몸(js/town.js의 hero, 스테이지의 Player)이 알리는 값 → pos 메시지. 마을·건물·스테이지 안에서만 보낸다
export type Body = { x: number; y: number; face: number; vx: number; vy: number; ground: boolean; dashT: number; sit: unknown };
const clampV = (v: number) => Math.max(-MAX_SPEED, Math.min(MAX_SPEED, Math.round(v || 0)));
export function animOf(b: Body): Anim { return b.sit ? 'sit' : b.dashT > 0 ? 'dash' : !b.ground ? 'jump' : Math.abs(b.vx) > 20 ? 'walk' : 'idle'; }
export function posOf(room: PlaceId, b: Body): PosFields {
  const st = animOf(b), r = placeBounds(room);
  return {
    room, x: Math.max(r.x0, Math.min(r.x1, Math.round(b.x))), y: Math.max(r.y0, Math.min(r.y1, Math.round(b.y))), face: b.face < 0 ? -1 : 1, st,
    vx: st === 'sit' ? 0 : clampV(b.vx), vy: st === 'sit' || st === 'idle' || st === 'walk' ? 0 : clampV(b.vy),   // 서 있거나 걸을 때는 땅 위라 위아래 속도가 없다
  };
}

// 원격 플레이어 위치 예측(dead reckoning): 마지막으로 받은 점에서 받은 속도로 앞질러 그리고, 새 점이 오면 어긋남을 서서히 줄인다
function extrapolate(r: Peer, now: number) {
  const room = r.room!, el = Math.min((now - r.bt) / 1000, EXTRAP_MAX), k = Math.exp(-(now - r.bt) / 1000 / ERR_TAU), b = placeBounds(room);
  const air = r.st === 'jump';   // 점프는 중력까지, 대시는 받은 속도 그대로, 땅 위에서는 위아래가 없다
  const dy = air ? r.vy * el + 0.5 * GRAVITY * el * el : r.st === 'dash' ? r.vy * el : 0;
  placePeer(r, Math.max(b.x0, Math.min(b.x1, r.bx + r.vx * el + r.ex * k)), Math.max(b.y0, Math.min(b.y1, r.by + dy + r.ey * k)));
}
export function updatePeers(dt: number) {
  const now = performance.now();
  for (const r of peers.values()) {
    if (r.room === null) continue;
    if (!HOOKS.peerPose.some((f) => f(r))) extrapolate(r, now);
    tickPeer(r, dt);
  }
}
// 이 장소(마을·건물·스테이지)에 지금 있는 사람들 (그릴 사람)
export const peersIn = (room: PlaceId) => [...peers.values()].filter((r) => r.room === room);
